import { getContext, setContext } from "svelte";
import {
    cancelSponsoredSubscriptionSE,
    createSponsoredBillingPortalSE,
    createSponsoredSetupSessionSE,
    createSponsoredSubscriptionSE,
    getSponsoredSubscriptionsSE,
    lookupSponsoredUserSE,
    reactivateSponsoredSubscriptionSE,
    type SponsoredBillingStatus,
    type SponsoredCreateResult,
    type SponsoredListResponse,
    type SponsoredLookupResult,
    type SponsoredSubscription,
    type SponsoredTarget,
} from "@/services/sponsored";
import { translate } from "@/utils/format";
import type { DashboardStore } from "./dashboard.svelte";

const RETURN_PATH = "/dashboard?section=sponsored";

const EMPTY_BILLING: SponsoredBillingStatus = {
    hasCustomer: false,
    hasDefaultPaymentMethod: false,
    paymentMethod: null,
};

/**
 * Subscriptions the gym pays for its users. Independent from Stripe Connect:
 * only needs a saved payment method. Loaded lazily by the section.
 */
export class SponsoredStore {
    items = $state<SponsoredSubscription[]>([]);
    totals = $state<SponsoredListResponse["totals"]>({
        active: 0,
        canceling: 0,
        pastDue: 0,
        monthlyCost: { amount: 0, currency: "eur" },
    });
    billing = $state<SponsoredBillingStatus>({ ...EMPTY_BILLING });
    loading = $state(false);
    loaded = $state(false);
    error = $state("");
    /** ids with an action in flight (cancel / reactivate). */
    busyIds = $state<string[]>([]);
    portalBusy = $state(false);

    /** Visible rows: canceled ones are history, not management. */
    visible = $derived(this.items.filter((s) => s.status !== "canceled"));
    /** userIds that can't be sponsored again. */
    sponsoredUserIds = $derived(
        new Set(
            this.visible.map((s) => s.userId).filter((v): v is string => !!v),
        ),
    );

    #dash: DashboardStore;

    constructor(dash: DashboardStore) {
        this.#dash = dash;
    }

    get #uid(): string {
        return String(this.#dash.user?.id || "");
    }

    #apply(res: SponsoredListResponse) {
        this.items = res.items;
        this.totals = res.totals;
        this.billing = res.billing ?? { ...EMPTY_BILLING };
    }

    load = async (force = false) => {
        if (!this.#uid) return;
        if (this.loading || (this.loaded && !force)) return;
        this.loading = true;
        this.error = "";
        try {
            this.#apply(await getSponsoredSubscriptionsSE(this.#uid));
            this.loaded = true;
        } catch (err) {
            console.error("❌ sponsored list error:", err);
            this.error = translate(
                "PARTNER_SPONSORED_LOAD_ERROR",
                "No hemos podido cargar las suscripciones. Inténtalo de nuevo.",
            );
        } finally {
            this.loading = false;
        }
    };

    refresh = () => this.load(true);

    /** Back from Stripe Checkout (setup): re-read what the webhook stored. */
    onBillingReturn = async (outcome: "success" | "cancel") => {
        if (outcome === "success") {
            await this.refresh();
            this.#dash.toast(
                this.billing.hasDefaultPaymentMethod
                    ? translate("PARTNER_BILLING_SAVED", "Método de pago guardado ✅")
                    : translate(
                          "PARTNER_BILLING_PENDING",
                          "Estamos confirmando tu método de pago ⏳",
                      ),
            );
        }
    };

    startSetup = async () => {
        if (this.portalBusy || !this.#uid) return;
        this.portalBusy = true;
        try {
            const res = await createSponsoredSetupSessionSE(
                this.#uid,
                `${RETURN_PATH}&billing=success`,
                `${RETURN_PATH}&billing=cancel`,
            );
            this.#go(res.url);
        } catch (err) {
            console.error("❌ sponsored setup error:", err);
            this.#dash.toast(this.#genericError());
        } finally {
            this.portalBusy = false;
        }
    };

    openPortal = async () => {
        if (this.portalBusy || !this.#uid) return;
        this.portalBusy = true;
        try {
            const res = await createSponsoredBillingPortalSE(this.#uid, RETURN_PATH);
            this.#go(res.url);
        } catch (err) {
            console.error("❌ sponsored portal error:", err);
            this.#dash.toast(this.#genericError());
        } finally {
            this.portalBusy = false;
        }
    };

    lookup = (target: SponsoredTarget): Promise<SponsoredLookupResult> =>
        lookupSponsoredUserSE(this.#uid, target);

    create = async (
        target: SponsoredTarget,
        idempotencyKey: string,
    ): Promise<SponsoredCreateResult> => {
        const res = await createSponsoredSubscriptionSE(
            this.#uid,
            target,
            idempotencyKey,
        );
        if (res.ok) {
            await this.refresh();
            // Auto-linked users change the attributed list and metrics.
            if (res.linkedNow) void this.#dash.loadMetrics();
        }
        return res;
    };

    cancel = (id: string) => this.#mutate(id, cancelSponsoredSubscriptionSE);
    reactivate = (id: string) =>
        this.#mutate(id, reactivateSponsoredSubscriptionSE);

    async #mutate(
        id: string,
        fn: typeof cancelSponsoredSubscriptionSE,
    ): Promise<boolean> {
        if (this.busyIds.includes(id)) return false;
        this.busyIds = [...this.busyIds, id];
        try {
            const res = await fn(this.#uid, id);
            if (!res.ok) {
                this.#dash.toast(this.#genericError());
                return false;
            }
            // Server is the source of truth for totals; swap the row now, then sync.
            this.items = this.items.map((s) => (s.id === id ? res.subscription : s));
            void this.refresh();
            return true;
        } catch (err) {
            console.error("❌ sponsored mutate error:", err);
            this.#dash.toast(this.#genericError());
            return false;
        } finally {
            this.busyIds = this.busyIds.filter((x) => x !== id);
        }
    }

    #genericError() {
        return translate(
            "PP_ERROR_GENERIC",
            "Ha ocurrido un error. Inténtalo de nuevo.",
        );
    }

    /** Same-origin paths (fake mode) stay in the SPA; Stripe URLs leave it. */
    #go(url: string) {
        if (!url) return;
        if (url.startsWith("/")) {
            window.history.replaceState(window.history.state, "", url);
            const outcome = new URLSearchParams(url.split("?")[1] || "").get(
                "billing",
            );
            if (outcome === "success" || outcome === "cancel") {
                void this.onBillingReturn(outcome);
                // Params are one-shot.
                const u = new URL(window.location.href);
                u.searchParams.delete("billing");
                window.history.replaceState(window.history.state, "", u);
            }
            return;
        }
        window.location.assign(url);
    }
}

const CTX = Symbol("partner-sponsored");

export function setSponsoredCtx(store: SponsoredStore) {
    setContext(CTX, store);
    return store;
}

export function getSponsoredCtx(): SponsoredStore {
    return getContext<SponsoredStore>(CTX);
}
